import type { LevelDef } from '@/types';

export const levels: Record<number, LevelDef> = {
  1: {
    id: 1,
    title: 'The Dungeon Entrance',
    description: 'Learn the basics of VoiceOver navigation to enter the dungeon.',
    objective: "Navigate to the 'Enter Cave' button using VO + Arrow keys",
    commands: ['moveNext', 'movePrevious'],
    layout: 'linear',
    elements: [
      { id: 'start', type: 'text', label: 'You stand before a dark cave.', isStart: true },
      { id: 'item1', type: 'button', label: 'Examine Torch' },
      { id: 'item2', type: 'button', label: 'Inspect Skeleton' },
      { id: 'item3', type: 'button', label: 'Read Warning Sign' },
      { id: 'goal', type: 'button', label: 'Enter Cave', isGoal: true },
    ],
    winCondition: { type: 'reach', target: 'goal' },
    starThresholds: { moves: [8, 6, 4] },
    hints: [
      'Press Control + Option + Right Arrow to move to the next item',
      'Keep pressing VO + → until you reach the Goal',
    ],
  },

  2: {
    id: 2,
    title: 'The Courtyard Grid',
    description: 'Navigate in all four directions across the courtyard tiles.',
    objective: "Navigate to the 'Stairs Down' tile in the grid",
    commands: ['moveNext', 'movePrevious', 'moveDown', 'moveUp'],
    layout: 'grid',
    gridColumns: 4,
    elements: [
      { id: 'a1', type: 'button', label: 'Mossy Tile', isStart: true },
      { id: 'a2', type: 'button', label: 'Cracked Tile' },
      { id: 'a3', type: 'button', label: 'Bloody Tile' },
      { id: 'a4', type: 'button', label: 'Clean Tile' },
      { id: 'b1', type: 'button', label: 'Trapdoor' },
      { id: 'b2', type: 'button', label: 'Rubble' },
      { id: 'b3', type: 'button', label: 'Stairs Down', isGoal: true },
      { id: 'b4', type: 'button', label: 'Puddle' },
      { id: 'c1', type: 'button', label: 'Statue' },
      { id: 'c2', type: 'button', label: 'Bones' },
      { id: 'c3', type: 'button', label: 'Chest' },
      { id: 'c4', type: 'button', label: 'Wall' },
    ],
    winCondition: { type: 'reach', target: 'b3' },
    starThresholds: { moves: [6, 4, 3] },
    hints: [
      'Use VO + ↓ to move down to the next row',
      'The stairs are in the second row, third column',
    ],
  },

  3: {
    id: 3,
    title: 'The Ancient Scroll',
    description: 'Use reading commands to uncover hidden lore.',
    objective: "Find and navigate to the passage containing the secret password 'LUMOS'",
    commands: ['moveNext', 'movePrevious', 'readAll', 'readFromBeginning'],
    layout: 'document',
    elements: [
      { id: 'h1', type: 'heading', label: 'Dusty Scroll', isStart: true },
      { id: 'p1', type: 'text', label: 'A tale of old kings and lost magic.' },
      { id: 'p2', type: 'text', label: 'To pass the guardian, speak the word.' },
      { id: 'p3', type: 'text', label: 'The secret password is LUMOS.', isGoal: true },
      { id: 'p4', type: 'text', label: 'Do not forget it.' },
      { id: 'p5', type: 'text', label: 'The scroll ends here.' },
    ],
    winCondition: { type: 'reach', target: 'p3' },
    starThresholds: { moves: [6, 4, 3] },
    hints: [
      'Use VO + A to read all content from your current position',
      "Reading all costs 0 moves! Listen for 'LUMOS', then navigate to it",
    ],
  },

  4: {
    id: 4,
    title: 'The Goblin Tent',
    description: 'Learn to interact with enclosed groups (tents).',
    objective: "Enter the tent and activate the 'Stolen Amulet' inside",
    commands: ['moveNext', 'movePrevious', 'interact', 'stopInteract', 'activate'],
    layout: 'nested',
    elements: [
      { id: 'intro', type: 'text', label: 'You spot a tent in the darkness.', isStart: true },
      {
        id: 'group1',
        type: 'group',
        label: 'Goblin Tent',
        children: [
          { id: 'inner1', type: 'button', label: 'Sleeping Goblin' },
          { id: 'inner2', type: 'button', label: 'Stolen Amulet', isGoal: true },
          { id: 'inner3', type: 'button', label: 'Rusty Sword' },
        ],
      },
      { id: 'outro', type: 'text', label: 'A locked chest sits outside.' },
    ],
    winCondition: { type: 'activate', target: 'inner2' },
    starThresholds: { moves: [6, 5, 4] },
    hints: [
      'Navigate to the Tent, then press VO + Shift + ↓ to enter (interact)',
      "Inside, find the Amulet and press VO + Space to take it (activate)",
    ],
  },

  5: {
    id: 5,
    title: 'The Labyrinth Signposts',
    description: 'Navigate quickly between headings (signposts).',
    objective: "Find and navigate to the 'North Exit' signpost",
    commands: ['moveNext', 'movePrevious', 'nextHeading', 'previousHeading'],
    layout: 'document',
    elements: [
      { id: 'h1', type: 'heading', label: 'South Entrance', isStart: true },
      { id: 'p1', type: 'text', label: 'A dead end with cobwebs.' },
      { id: 'p2', type: 'text', label: 'More empty corridors.' },
      { id: 'h2', type: 'heading', label: 'West Wing' },
      { id: 'p3', type: 'text', label: 'You hear scurrying rats.' },
      { id: 'p4', type: 'text', label: 'A collapsed ceiling blocks the path.' },
      { id: 'h3', type: 'heading', label: 'East Armory' },
      { id: 'p5', type: 'text', label: 'Empty weapon racks.' },
      { id: 'h4', type: 'heading', label: 'North Exit', isGoal: true },
      { id: 'p6', type: 'text', label: 'Fresh air blows from here.' },
      { id: 'h5', type: 'heading', label: 'Secret Room' },
      { id: 'p7', type: 'text', label: 'Nothing but dust.' },
    ],
    winCondition: { type: 'reach', target: 'h4' },
    starThresholds: { moves: [6, 4, 3] },
    hints: [
      'Use VO + Command + H to jump to the next signpost (heading)',
      'This skips all the dead ends instantly',
    ],
  },

  6: {
    id: 6,
    title: 'The Portal Network',
    description: 'Navigate through portals (links) and activate the correct one',
    objective: "Find and activate the 'Crystal Portal'",
    commands: ['moveNext', 'movePrevious', 'nextLink', 'previousLink', 'activate'],
    layout: 'document',
    elements: [
      { id: 'h1', type: 'heading', label: 'Portal Chamber', isStart: true },
      { id: 'l1', type: 'link', label: 'Fire Portal' },
      { id: 'l2', type: 'link', label: 'Water Portal' },
      { id: 'p1', type: 'text', label: 'The water portal hums softly.' },
      { id: 'l3', type: 'link', label: 'Earth Portal' },
      { id: 'l4', type: 'link', label: 'Crystal Portal', isGoal: true },
      { id: 'p2', type: 'text', label: 'This one seems safe.' },
      { id: 'l5', type: 'link', label: 'Void Portal' },
      { id: 'l6', type: 'link', label: 'Bone Portal' },
    ],
    winCondition: { type: 'activate', target: 'l4' },
    starThresholds: { moves: [6, 5, 4] },
    hints: [
      'Use VO + Command + L to jump between portals (links)',
      "When you find 'Crystal Portal', press VO + Space to enter it",
    ],
  },

  7: {
    id: 7,
    title: 'The Master Key',
    description: 'Master the VoiceOver Rotor for ultimate navigation.',
    objective: "Use the Rotor to teleport directly to the 'Treasure Room' heading",
    commands: ['openRotor'],
    layout: 'complex',
    elements: [
      { id: 'h1', type: 'heading', label: 'Grand Hall', isStart: true },
      { id: 'l1', type: 'link', label: 'Kitchens' },
      { id: 'l2', type: 'link', label: 'Barracks' },
      { id: 'p1', type: 'text', label: 'Guards are patrolling.' },
      { id: 'h2', type: 'heading', label: 'Library' },
      { id: 'p2', type: 'text', label: 'Books line the walls.' },
      { id: 'l3', type: 'link', label: 'Read Grimoire' },
      { id: 'h3', type: 'heading', label: 'Treasure Room', isGoal: true },
      { id: 'p3', type: 'text', label: 'Mountains of gold!' },
      { id: 'h4', type: 'heading', label: 'Dungeon Cell' },
      { id: 'l4', type: 'link', label: 'Free Prisoner' },
    ],
    winCondition: { type: 'reach', target: 'h3' },
    starThresholds: { moves: [3, 2, 1] },
    hints: [
      'Press VO + U to open the Rotor (0 moves!)',
      "Use ← and → to change categories to 'Headings'",
      "Use ↓ to find 'Treasure Room' and press Enter to teleport (1 move)",
    ],
  },

  8: {
    id: 8,
    title: 'Shadow Sprint',
    description: 'Enable Quick Nav for rapid, single-key movement',
    objective: "Use Quick Nav to reach the 'Sanctuary' heading in under 4 moves",
    commands: ['toggleQuickNav'],
    layout: 'document',
    elements: [
      { id: 'h1', type: 'heading', label: 'Dark Alley', isStart: true },
      { id: 'p1', type: 'text', label: 'Shadows are chasing you. Move fast!' },
      { id: 'l1', type: 'link', label: 'Hide in Barrel' },
      { id: 'h2', type: 'heading', label: 'Market Square' },
      { id: 'p2', type: 'text', label: 'The monsters are closing in.' },
      { id: 'l2', type: 'link', label: 'Climb Roof' },
      { id: 'h3', type: 'heading', label: 'Bridge' },
      { id: 'p3', type: 'text', label: 'Almost safe!' },
      { id: 'h4', type: 'heading', label: 'Sanctuary', isGoal: true },
      { id: 'p4', type: 'text', label: 'You made it to the church.' },
    ],
    winCondition: { type: 'reach', target: 'h4' },
    starThresholds: { moves: [6, 4, 3] },
    hints: [
      'Press VO + Q to enable Quick Nav (0 moves)',
      'With Quick Nav on, just press H to jump to the next heading',
      'No need to hold VO in Quick Nav mode!',
    ],
  },

  9: {
    id: 9,
    title: 'The Alchemist\'s Grid',
    description: 'Navigate complex data tables to find the right potion.',
    objective: "Find the cell containing 'Elixir' in the potion rack",
    commands: ['moveNext', 'movePrevious', 'interact', 'stopInteract', 'moveDown', 'moveUp'],
    layout: 'table',
    elements: [
      { id: 'intro', type: 'text', label: 'A rack of glowing potions stands before you.', isStart: true },
      {
        id: 'table1',
        type: 'table',
        label: 'Potion Rack',
        rows: [
          ['Color', 'Shape', 'Contents'],
          ['Red', 'Round', 'Poison'],
          ['Blue', 'Square', 'Elixir'],
          ['Green', 'Tall', 'Acid'],
        ],
        goalCell: [2, 2],
      },
    ],
    winCondition: { type: 'reachCell', target: 'table1', cell: [2, 2] },
    starThresholds: { moves: [10, 6, 4] },
    hints: [
      'Interact with the table to enter the grid',
      'Use arrow keys to move between rows and columns',
      'The Elixir is in row 3, column 3 (Blue, Square, Elixir)',
    ],
  },

  10: {
    id: 10,
    title: 'The Sphinx\'s Riddle',
    description: 'Navigate and complete the final trial.',
    objective: "Jump through the riddle form and activate 'Unlock Gate'",
    commands: ['moveNext', 'movePrevious', 'nextFormControl', 'previousFormControl', 'activate'],
    layout: 'form',
    elements: [
      { id: 'h1', type: 'heading', label: 'The Sphinx Speaks', isStart: true },
      { id: 'label1', type: 'text', label: 'Speak your Name:' },
      { id: 'input1', type: 'input', label: 'Name field' },
      { id: 'label2', type: 'text', label: 'Answer my Riddle:' },
      { id: 'input2', type: 'input', label: 'Riddle field' },
      { id: 'label3', type: 'text', label: 'Swear an oath:' },
      { id: 'check1', type: 'checkbox', label: 'I swear by the old gods' },
      { id: 'p1', type: 'text', label: 'Choose wisely.' },
      { id: 'btn1', type: 'button', label: 'Flee in Terror' },
      { id: 'btn2', type: 'button', label: 'Unlock Gate', isGoal: true },
    ],
    winCondition: { type: 'activate', target: 'btn2' },
    starThresholds: { moves: [8, 6, 5] },
    hints: [
      'Use VO + Command + J to jump directly between form controls',
      'This skips the labels and goes straight to inputs and buttons',
      'Jump to Unlock Gate and press VO + Space to win!',
    ],
  },
};

export const TOTAL_LEVELS = Object.keys(levels).length;

export function getLevel(id: number): LevelDef | undefined {
  return levels[id];
}

